import React from 'react'

import { renderComponent } from '../../src/utils/components'
import { Layout } from '../../src/components'
import { useGlobalData } from '../_app'
import { getAvailablePaths, getSiteAlternateUrl } from '../../build-helpers/utils'
import { getGeneratedPaths } from '../../src/utils/getGeneratedPaths'
import { getContentBySlug } from '@/build-helpers/page'
import { getArticleCategoryByLocale } from '@/build-helpers/article'
import { getMarketHeader } from '@/build-helpers/market'
import ErrorBoundary from '@/src/components/errorBoundary/ErrorBoundary'

export async function getStaticPaths() {
  // If not production, don't build paths
  if (process.env.BUILD_ENV !== 'production' || process.env.DISABLE_PATHS_PAGE == 1) {
    return {
      paths: [],
      fallback: 'blocking'
    }
  }

  try {
    let paths = []

    const generatedPaths = getGeneratedPaths('page')

    for (let path in generatedPaths) {
      const markets = generatedPaths[path]
      for (let market in markets) {
        const locales = markets[market]

        for (let locale in locales) {
          // Only generate paths for locales where this page actually exists
          const pageData = locales[locale]
          if (pageData && pageData.slug) {
            const params = {
              slug: pageData.slug?.split('/') || []
            }

            // Skip the customers path as it's handled by pages/customers/[slug].js
            // This prevents double building of the customers page
            if (params.slug.length === 2 && params.slug[0] === 'customers') {
              continue
            }

            // Skip the blog path as it's handled by pages/blog/[category]/[...slug].js
            // This prevents double building of the blog page
            if (params.slug.length >= 2 && params.slug[0] === 'blog') {
              continue
            }

            paths.push({
              params: params,
              locale: pageData.slugPrefix
            })
          }
        }
      }
    }

    return {
      paths: paths,
      fallback: false // Set fallback to true to render pages on-demand
    }
  } catch (e) {
    console.error('Error in getStaticPaths', e)
    // Return blocking fallback to prevent NoFallbackError
    return {
      paths: [],
      fallback: 'blocking'
    }
  }
}

export async function getStaticProps({ params, locale }) {
  // Ignore default locale
  if (locale === 'default') {
    return {
      notFound: true
    }
  }

  const { slug } = params
  const slugPrefix = locale
  const language = locale.split('-')[0]

  // Normalize the slug by removing double slashes and trimming
  let pageSlug = Array.isArray(slug) ? slug.join('/') : slug
  pageSlug = pageSlug.replace(/\/+/g, '/').replace(/^\/|\/$/g, '')

  // Check if this page exists for this specific locale
  /* if (process.env.BUILD_ENV !== 'production') {
    const generatedPaths = getGeneratedPaths('page')
    let pageExistsForLocale = false

    for (let path in generatedPaths) {
      const markets = generatedPaths[path]
      for (let market in markets) {
        const locales = markets[market]
        for (let localeKey in locales) {
          const pageData = locales[localeKey]
          if (pageData && pageData.slug === pageSlug && pageData.slugPrefix === slugPrefix) {
            pageExistsForLocale = true
            break
          }
        }
        if (pageExistsForLocale) break
      }
      if (pageExistsForLocale) break
    }

    if (!pageExistsForLocale) {
      return { notFound: true }
    }
  } */

  let pageContext = await getMarketHeader(language, slugPrefix)
  if (!pageContext || !pageContext.market?.id) {
    return {
      notFound: true
    }
  }

  const pageContent = await getContentBySlug(pageSlug, language, pageContext.market.id)

  const alternativeHeaderId = pageContent?.alternativeHeader?.id || null

  if (alternativeHeaderId) {
    const alternativePageContext = await getMarketHeader(language, slugPrefix, alternativeHeaderId)
    // Only update pageContext if alternative header fetch was successful
    if (alternativePageContext && alternativePageContext.market?.id) {
      pageContext = alternativePageContext
    }
  }

  let articleCategories = []

  if (pageSlug === 'blog') {
    articleCategories = await getArticleCategoryByLocale(language)
  }

  if (!pageContent) {
    return {
      notFound: true,
      props: {
        locale: language,
        slugPrefix,
        page: {
          id: 'not-found'
        },
        header: pageContext?.header,
        market: pageContext?.market,
        footer: pageContext?.footer,
        articleCategories: [],
        articles: []
      }
    }
  }

  return {
    props: {
      locale: language,
      slugPrefix,
      page: { ...pageContent },
      availablePaths: getAvailablePaths({
        slugLocales: pageContent?._allSlugLocales,
        enabledLocales: pageContent?._allEnabledLocalesLocales,
        enabledMarkets: pageContent?.enabledMarkets
      }),
      header: pageContext?.header,
      market: pageContext?.market,
      footer: pageContext?.footer,
      articleCategories: articleCategories || [],
      articles: []
    }
  }
}

/* This layout is used for the following content models:
- Pages
- Integration Categories 
*/

const getSiteAlternates = (paths) => {
  const alternateUrls = []
  paths.forEach((path) => {
    const siteUrl = getSiteAlternateUrl(`${path.slugPrefix}/${path.slug}`)
    alternateUrls.push({
      rel: 'alternate',
      hrefLang: path.slugPrefix,
      href: siteUrl
    })
  })

  return alternateUrls
}

export const DynamicPage = ({ slugPrefix, locale, page, availablePaths }) => {
  const global = useGlobalData()
  const { components, metaTags, metaImage, marketsMetaTags } = page

  if (!page.id) {
    console.error('Error Fetching: ', slugPrefix)
    return
  }

  const alternateUrls = getSiteAlternates(availablePaths)

  const renderComponents = components?.map((component, idx) =>
    renderComponent({
      component,
      extra: {
        locale,
        slugPrefix
      },
      idx
    })
  )
  return (
    <ErrorBoundary>
      <Layout
        seoData={{
          marketsMetaTags: marketsMetaTags,
          metaTags: metaTags,
          metaImage: metaImage,
          alternate: alternateUrls
        }}
        slugPrefix={slugPrefix}
        allMarkets={global?.markets}
        paths={availablePaths}
      >
        {renderComponents}
      </Layout>
    </ErrorBoundary>
  )
}

export default DynamicPage
